// RFC 5545 iCalendar generator for single-day care-home events (update 2026_04.22.4)
//
// Produces a .ics payload that can be saved and opened by iOS Calendar, Android
// Google Calendar, Samsung Calendar, Outlook, Apple Calendar, etc. All UTC-less
// times are tagged with TZID=Europe/London and accompanied by an inline
// VTIMEZONE component so viewers in any timezone see the correct local time.

export interface IcsEventInput {
    event_id: string;
    event_name: string;
    event_description: string;
    datetime_start: string;
    datetime_end: string;
    contact_name: string;
    contact_phone: string;
    contact_email: string;
}

export interface IcsLocationInput {
    carehome_name: string;
    address_1: string;
    address_2: string;
    town: string;
    county: string;
    post_code: string;
    latitude: number;
    longitude: number;
    regulator_id: string;
}

const CRLF = '\r\n';

// Parse "YYYY-MM-DD HH:MM" or "YYYY_MM_DD HH:MM" to { YYYYMMDD, HHMMSS } local parts.
const parseLocalDateTime = (s: string): { date: string; time: string } | null => {
    if (!s) return null;
    const m = s.replace(/_/g, '-').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (!m) return null;
    return {
        date: `${m[1]}${m[2]}${m[3]}`,
        time: `${m[4]}${m[5]}${m[6] || '00'}`
    };
};

// Current time as UTC basic-format YYYYMMDDTHHMMSSZ.
const utcStamp = (): string => {
    const d = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    return (
        `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
        `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
    );
};

// Escape per RFC 5545 §3.3.11: backslash, semicolon, comma, and newline.
const escapeText = (v: string): string =>
    (v || '')
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r\n|\n|\r/g, '\\n');

// Fold lines to a max of 75 octets with CRLF + single leading space per RFC 5545.
// We count UTF-8 bytes rather than code units so multi-byte characters are safe.
const foldLine = (line: string): string => {
    const enc = new TextEncoder();
    const bytes = enc.encode(line);
    if (bytes.length <= 75) return line;

    const dec = new TextDecoder();
    const chunks: string[] = [];
    let offset = 0;
    while (offset < bytes.length) {
        const size = offset === 0 ? 75 : 74; // continuation lines reserve 1 octet for leading space
        let end = Math.min(offset + size, bytes.length);
        // Don't split inside a UTF-8 code point
        while (end > offset && (bytes[end - 1] & 0xC0) === 0x80) end--;
        chunks.push(dec.decode(bytes.subarray(offset, end)));
        offset = end;
    }
    return chunks.join(CRLF + ' ');
};

const joinLines = (lines: string[]): string => lines.map(foldLine).join(CRLF) + CRLF;

const composeLocation = (loc: IcsLocationInput): string => {
    const parts = [
        loc.carehome_name,
        loc.address_1,
        loc.address_2,
        loc.town,
        loc.county,
        loc.post_code
    ].map(p => (p || '').trim()).filter(Boolean);
    return parts.join(', ');
};

const composeDescription = (ev: IcsEventInput, loc: IcsLocationInput): string => {
    const lines: string[] = [];
    if (ev.event_description && ev.event_description.trim()) {
        lines.push(ev.event_description.trim());
        lines.push('');
    }
    if (loc.carehome_name) lines.push(loc.carehome_name);
    const contactBits: string[] = [];
    if (ev.contact_name) contactBits.push(`Contact: ${ev.contact_name}`);
    if (ev.contact_phone) contactBits.push(`Phone: ${ev.contact_phone}`);
    if (ev.contact_email) contactBits.push(`Email: ${ev.contact_email}`);
    if (contactBits.length) lines.push(...contactBits);

    if (loc.regulator_id) {
        lines.push('');
        lines.push(`https://map.openscore.org.uk/?carehome=${encodeURIComponent(loc.regulator_id)}`);
    }
    return lines.join('\n');
};

const slugify = (s: string): string =>
    (s || 'event')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'event';

export const buildIcsContent = (ev: IcsEventInput, loc: IcsLocationInput): string => {
    const start = parseLocalDateTime(ev.datetime_start);
    const end = parseLocalDateTime(ev.datetime_end);
    if (!start || !end) {
        throw new Error('Invalid event start or end datetime');
    }

    const uid = `${ev.event_id || `${start.date}T${start.time}-${loc.regulator_id}`}@openscore.org.uk`;
    const summary = escapeText(ev.event_name || 'Care home event');
    const description = escapeText(composeDescription(ev, loc));
    const locationText = escapeText(composeLocation(loc));

    const lines: string[] = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//OpenScore//OSMap//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        // VTIMEZONE for Europe/London (GMT/BST)
        'BEGIN:VTIMEZONE',
        'TZID:Europe/London',
        'BEGIN:STANDARD',
        'DTSTART:19710101T020000',
        'TZOFFSETFROM:+0100',
        'TZOFFSETTO:+0000',
        'RRULE:FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU',
        'TZNAME:GMT',
        'END:STANDARD',
        'BEGIN:DAYLIGHT',
        'DTSTART:19710101T010000',
        'TZOFFSETFROM:+0000',
        'TZOFFSETTO:+0100',
        'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=-1SU',
        'TZNAME:BST',
        'END:DAYLIGHT',
        'END:VTIMEZONE',
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${utcStamp()}`,
        `DTSTART;TZID=Europe/London:${start.date}T${start.time}`,
        `DTEND;TZID=Europe/London:${end.date}T${end.time}`,
        `SUMMARY:${summary}`,
        `DESCRIPTION:${description}`
    ];

    if (locationText) lines.push(`LOCATION:${locationText}`);
    if (Number.isFinite(loc.latitude) && Number.isFinite(loc.longitude) && (loc.latitude !== 0 || loc.longitude !== 0)) {
        lines.push(`GEO:${loc.latitude.toFixed(6)};${loc.longitude.toFixed(6)}`);
    }
    if (ev.contact_email) {
        const cn = ev.contact_name ? `;CN=${escapeText(ev.contact_name)}` : '';
        lines.push(`ORGANIZER${cn}:mailto:${ev.contact_email}`);
    }

    lines.push('END:VEVENT');
    lines.push('END:VCALENDAR');

    return joinLines(lines);
};

export const downloadIcs = (filename: string, content: string): void => {
    const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    // iOS Safari requires the element to be in the DOM for the synthetic click.
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    // Give the browser a tick to start the download/open before revoking.
    setTimeout(() => URL.revokeObjectURL(url), 2000);
};

export const makeIcsFilename = (eventName: string): string =>
    `openscore-${slugify(eventName)}.ics`;
